import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Node, Document, Branch, Root } from '@xanadu/model';
import { UrlHelper } from '../url-helper';

@Injectable({
    providedIn: 'root'
})
export class DataService {
    constructor(
        private readonly http: HttpClient,
        private readonly urlHelper: UrlHelper
    ) {
    }

    public getRoot(): Observable<Root> {
        // return this.data$.pipe(map(_ => this.root))
        return this.http.get<Root>(this.urlHelper.of('node/root')).pipe(
            map(result => new Root(result)));
    }

    public getNode(id: string): Observable<any> {
        // return this.data$.pipe(map(_ => this.root))
        return this.http.get(this.urlHelper.of(`node/${id}`));
    }

    public checkGraphQL(id: string, graphqlType: string, site?: string): Observable<boolean> {
        const checkUrl = site?.length ? `graphql/genus/${id}/${graphqlType}/${site}` : `graphql/${id}/${graphqlType}`;
        return this.http.get<boolean>(this.urlHelper.of(checkUrl));
    }

    public all<T extends Document>(type: new (x: any) => T): Observable<T[]> {
        return this.http.get<T[]>(this.urlHelper.for(type)).pipe(
            map(data => data.map(item => new type(item)))
        );
    }

    public locate(id: string): Observable<string[]> {
        return this.http.get<string[]>(this.urlHelper.of(`node/locate/${id}`));
    }

    public save<T extends Node>(node: T, action?: (model: any) => void): Observable<T> {
        const type = Object.getPrototypeOf(node).constructor;
        const model = node.model;

        if (action) {
            action(model);
        }

        return this.http.post<T>(this.urlHelper.for(type), model).pipe(
            map(doc => {
                if (doc?._id) {
                    node.accept(doc._id);
                }
                return node;
            }));
    }

    public delete<T extends Node>(node: T): Observable<T> {
        const type = Object.getPrototypeOf(node).constructor;

        if (node.isNew) {
            this.deleteParent(node);
        } else if (node._id?.length) {
            return this.http.delete<T>(`${this.urlHelper.for(type)}/${node._id}`).pipe(
                map(result => {
                    if (result) {
                        this.deleteParent(node);
                    }
                    return node;
                }));
        }

        return of(node);
    }

    public discard<T extends Node>(document: T): void {
        this.deleteParent(document);
    }

    public replace<T extends Node>(node: T, branch: Branch): Observable<T> {
        const observable = node.isNew
            ? of(node)
            : this.save(node, model => model.parentId = branch._id);
        return observable.pipe(
            tap(_ => {
                this.deleteParent(node);
                node.parentId = branch._id;
                node.parent = branch;
            })
        );
    }

    public post<T extends Node>(type: new (x: any) => T, data: any, subUrl?: string): Observable<any> {
        let url = this.urlHelper.for(type);
        if (subUrl?.length) {
            url = `${url}/${subUrl}`;
        }
        return this.http.post<any>(url, data);
    }

    private deleteParent(node: Node, parent?: Branch): void {
        if (!parent) {
            parent = node.parent;
        }
        if (parent) {
            if (node instanceof Branch && parent.branches) {
                const index = parent.branches.findIndex(x => x._id === node._id);
                if (index > -1) {
                    parent.branches.splice(index, 1);
                }
            } else if (parent.nodes) {
                const index = parent.nodes.findIndex(x => x._id === node._id);
                if (index > -1) {
                    parent.nodes.splice(index, 1);
                }
            }
        }
    }
}
